import { fetchProductCombos } from '../../../../catalog/public-api';
import { supabase } from '../../../../shared/supabase/supabaseClient';
import { getSellableLotQuantity } from '../guards/fefoAllocator';
import { findStockRow } from './stockRowLookup';

export type Availability = {
  /** Aggregated on-hand in stocks.quantity (already net of active online reservations). */
  quantity: number;
  /** Units that can be sold now (FEFO-aware when lots exist and no variantId). */
  sellable: number;
  /** quantity − sellable when lots track expiry; else 0. */
  expiredUnits: number;
};

async function getComboSellableAvailability(
  branchId: string,
  comboProductId: string,
): Promise<Availability | null> {
  const combos = await fetchProductCombos([comboProductId]);
  if (!combos.has(comboProductId)) return null;

  const { data, error } = await supabase.rpc('catalog_combo_sellable_qty', {
    p_branch_id: branchId,
    p_combo_product_id: comboProductId,
  });
  if (error) {
    console.warn('[getAvailability] catalog_combo_sellable_qty', error.message);
    return null;
  }
  const sell = Math.max(0, Number(data ?? 0));
  return { quantity: sell, sellable: sell, expiredUnits: 0 };
}

export async function getAvailability(
  branchId: string,
  productId: string,
  variantId?: string | null,
): Promise<Availability> {
  if (!variantId) {
    const comboAvail = await getComboSellableAvailability(branchId, productId);
    if (comboAvail) return comboAvail;
  }

  const stockRow = await findStockRow(productId, branchId, variantId);
  const quantity = stockRow?.quantity ?? 0;

  if (variantId) {
    return { quantity, sellable: quantity, expiredUnits: 0 };
  }

  const sellableLots = await getSellableLotQuantity(productId, branchId);
  // #1497: con divergencia agregado↔lotes los lotes pueden sobre-declarar
  // stock (ej. ajustes que no consumieron lotes). Lo vendible nunca puede
  // superar el agregado físico — el cap es la red de seguridad anti-sobreventa.
  const sellable = sellableLots !== null ? Math.min(sellableLots, quantity) : quantity;
  const expiredUnits = sellableLots !== null ? Math.max(0, quantity - sellable) : 0;
  return { quantity, sellable, expiredUnits };
}
